import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["height", "width"]

import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  watch,
} from 'vue';
import { fabric } from 'fabric';
import 'fabric-history';
import {
  FabricExposition,
  injectionKey,
} from '@/core/components/data-display/Fabric/fabric.shared';
import { useResizeObserver } from '@/hooks/useResizeObserver';


export default /*#__PURE__*/_defineComponent({
  __name: 'NkFabric',
  props: {
  maxZoom: {
    type: Number,
    default: Infinity,
  },
  minZoom: {
    type: Number,
    default: 0,
  },
  options: {
    type: Object as PropType<fabric.ICanvasOptions>,
    default: () => ({}),
  },
},
  emits: ['ready'],
  setup(__props, { expose, emit }) {

const props = __props





const el = ref();
const canvasContainer = ref();
let canvas: fabric.Canvas;
const getCanvas = () => canvas;
const {
  width,
  height,
} = useResizeObserver(canvasContainer);

const ready = ref(false);
const isHoldingSpace = ref(false);
const isHoldingClick = ref(false);
/** See here for solutions:
 * https://stackoverflow.com/questions/30466322/fabricjs-pan-and-zoom
 */
const zoomIn = (point: fabric.Point) => {
  const zoomLevel = canvas.getZoom();
  const newZoomLevel = zoomLevel + zoomLevel / 5;
  if (newZoomLevel > props.maxZoom) {
    return;
  }
  canvas.zoomToPoint(point, newZoomLevel);
  canvas.setZoom(newZoomLevel);
};

const zoomOut = (point: fabric.Point) => {
  const zoomLevel = canvas.getZoom();
  const newZoomLevel = zoomLevel - zoomLevel / 5;
  if (newZoomLevel < props.minZoom) {
    return;
  }
  canvas.zoomToPoint(point, newZoomLevel);
  canvas.setZoom(newZoomLevel);
};

const undo = () => {
  // console.log('undo');
  // (canvas as any).undo();
};

const redo = () => {
  // console.log('redo');
  // (canvas as any).redo();
};

const onShortKey = (e: any) => {
  switch (e.srcKey) {
    case 'undo':
      undo();
      break;
    case 'redo':
      redo();
      break;
    default:
      break;
  }
};

const onCanvasKeyDown = (e: KeyboardEvent) => {
  if (e.key === ' ' && !isHoldingClick.value) {
    isHoldingSpace.value = true;
  }
};

const onCanvasKeyUp = (e: KeyboardEvent) => {
  if (e.key === ' ') {
    isHoldingSpace.value = false;
  }
};

const onCanvasMouseUp = (e: fabric.IEvent<MouseEvent>) => {
  isHoldingClick.value = false;
};

const onCanvasMouseDown = (e: fabric.IEvent<MouseEvent>) => {
  isHoldingClick.value = true;
};

const onCanvasMouseMove = (e: fabric.IEvent<MouseEvent>) => {
  if (isHoldingSpace.value && isHoldingClick.value && e && e.e) {
    const delta = new fabric.Point(e.e.movementX, e.e.movementY);
    canvas.relativePan(delta);
  }
};

const onCanvasMouseWheel = (e: fabric.IEvent<WheelEvent>) => {
  e.e.stopPropagation();
  e.e.preventDefault();
  const delta = e.e.deltaY;
  if (delta !== 0) {
    const pointer = canvas.getPointer(e.e, true);
    const point = new fabric.Point(pointer.x, pointer.y);
    if (delta > 0) {
      zoomOut(point);
    } else if (delta < 0) {
      zoomIn(point);
    }
  }
};

const addEventListeners = () => {
  document.addEventListener('keydown', onCanvasKeyDown);
  document.addEventListener('keyup', onCanvasKeyUp);
  canvas.on('mouse:down', onCanvasMouseDown);
  canvas.on('mouse:up', onCanvasMouseUp);
  canvas.on('mouse:move', onCanvasMouseMove);
  canvas.on('mouse:wheel', onCanvasMouseWheel);

  emit('ready', getCanvas());
};

const removeEventListeners = () => {
  document.removeEventListener('keydown', onCanvasKeyDown);
  document.removeEventListener('keyup', onCanvasKeyUp);
  canvas.off('mouse:down');
  canvas.off('mouse:up');
  canvas.off('mouse:move');
  canvas.off('mouse:wheel');
};

watch(isHoldingSpace, () => {
  canvas.discardActiveObject();
  canvas.renderAll();
});

watch([isHoldingSpace, isHoldingClick], () => {
  if (isHoldingSpace.value && isHoldingClick.value) {
    canvas.setCursor('grabbing');
    canvas.defaultCursor = 'grabbing';
  } else if (isHoldingSpace.value) {
    canvas.setCursor('grab');
    canvas.defaultCursor = 'grab';
  } else {
    canvas.setCursor('default');
    canvas.defaultCursor = 'default';
  }
});

watch(isHoldingClick, () => {
  if (isHoldingClick.value) {
    canvas.setCursor('grabbing');
  } else {
    canvas.setCursor('grab');
  }
});

watch([width, height], () => {
  if (canvas) {
    canvas.setDimensions({
      width: width.value,
      height: height.value,
    });
  }
});

onMounted(() => {
  nextTick(() => {
    canvas = new fabric.Canvas(el.value, {
      preserveObjectStacking: true,
      enableRetinaScaling: true,
      ...props.options,
    });
    ready.value = true;
    addEventListeners();
  });
});

onBeforeUnmount(() => {
  removeEventListeners();
});

const exposed = {
  getCanvas,
  isHoldingSpace,
};
provide<FabricExposition>(injectionKey, exposed);
expose<FabricExposition>(exposed);

return (_ctx: any,_cache: any) => {
  const _directive_shortkey = _resolveDirective("shortkey")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    ref_key: "canvasContainer",
    ref: canvasContainer,
    class: _normalizeClass([
    isHoldingSpace.value && '!cursor-grab',
    isHoldingSpace.value && isHoldingClick.value && '! cursor-grabbing',
  ]),
    onShortkey: onShortKey
  }, [
    _createElementVNode("canvas", {
      ref_key: "el",
      ref: el,
      height: _unref(height),
      width: _unref(width)
    }, null, 8, _hoisted_1),
    (ready.value)
      ? _renderSlot(_ctx.$slots, "default", { key: 0 })
      : _createCommentVNode("", true)
  ], 34)), [
    [_directive_shortkey, {
    undo: ['ctrl', 'z'],
    redo: ['ctrl', 'shift', 'z'],
  }]
  ])
}
}

})